import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useQuery } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Period } from 'screens/Analytics/constants'

interface GenderDistribution {
  gender_distribution: {
    male: number
    female: number
    unknown: number
  }
}

interface AgeGenderDistribution {
  age_gender_distribution: Array<{
    range: string
    male: number
    female: number
    unknown: number
  }>
}

interface GeographicDistribution {
  geographic_distribution: Array<{
    code: string
    percentage: number
  }>
  error_title?: string
  error_message?: string
}

interface TopCities {
  top_cities: Array<{
    name: string
    percentage: number
  }>
  error_title?: string
  error_message?: string
}

interface TopLanguages {
  top_languages: Array<{
    code: string
    percentage: number
  }>
}

export interface DemographicsData {
  gender: GenderDistribution
  age_gender: AgeGenderDistribution
  country: GeographicDistribution
  city: TopCities
  languages: TopLanguages
}

const fetchDemographics = async (workspaceId: number, integrationId: number, metrics: string[], period: Period) => {
  const response = await sendToApi<DemographicsData>(
    `/w/${workspaceId}/external_integrations/${integrationId}/audience_metrics/demographics`,
    {
      method: 'GET',
      params: {
        metrics: metrics.join(','),
        period: period,
      },
    },
  )

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(response.data)
  }

  return response.data
}

export const useDemographics = (integrationId: number, metrics: string[], period: Period) => {
  const workspaceId = useCurrentWorkspaceId()

  return useQuery<DemographicsData, Error>({
    queryKey: ['demographics', integrationId, metrics],
    queryFn: () => fetchDemographics(workspaceId, integrationId, metrics, period),
    refetchOnWindowFocus: false,
  })
}
